<template>
  <div
    class="w-full h-full px-4 tablet-h:px-5 pt-[14px] pb-[22px] bg-gray-200/20 rounded-xl"
  >
    <div class="flex items-center gap-x-2.5 mb-2">
      <VideoIcon v-if="canShowPlayIcon" class="size-[22px] text-gray-800" />

      <div class="font-medium text-[15px] leading-[20px] text-gray-800">
        Video
      </div>
    </div>

    <div class="w-full h-px mb-4 bg-gray-200"></div>

    <MatchMarkers :markers="match.markers" :side="side" />

    <!-- Full timeline filmstrip with the matching segment highlighted -->
    <div class="text-[12px] leading-[16px] text-gray-500 mb-[3px]">
      Matching segment
    </div>

    <TickerTimeline
      v-if="hasTimeline && duration"
      :duration="duration"
      class="mb-[8px]"
    />

    <div ref="timelineContainer" class="relative h-[50px] mb-[15px]">
      <div class="relative flex h-full items-center gap-x-px overflow-hidden">
        <!-- Hidden measurement image -->
        <img
          v-if="!timelineFramesReady"
          :src="timelineFrames[0]"
          class="h-[56px] invisible absolute"
          @load="onTimelineFrameLoad"
        />

        <img
          v-for="(frameUrl, index) in distributedTimelineFrames"
          :key="index"
          :src="frameUrl"
          loading="lazy"
          decoding="async"
          class="h-[56px] shrink-0"
          :style="{ width: timelineFrameWidth + 'px' }"
        />
      </div>

      <div
        v-if="hasTimeline"
        class="absolute inset-y-0 left-0 bg-white/70 pointer-events-none"
        :style="{ width: fadeLeftWidth }"
      ></div>

      <div
        v-if="hasTimeline"
        class="absolute inset-y-0 right-0 bg-white/70 pointer-events-none"
        :style="{ width: fadeRightWidth }"
      ></div>

      <!-- Match highlight overlay -->
      <div
        v-if="hasTimeline"
        class="absolute top-0 h-full border-2 border-y-0 rounded-[3px]"
        :class="borderClass"
        :style="{
          left: highlightLeft + '%',
          width: highlightWidth + '%',
        }"
      >
        <div class="absolute inset-0" :class="fillClass"></div>

        <MatchTag
          :id="number"
          :level="match.level"
          class="absolute -top-2 -left-2"
        />
      </div>

      <!-- Centered under the highlight, clamped inside the timeline -->
      <div
        v-if="hasTimeline"
        ref="pillEl"
        class="absolute top-[calc(100%+2px)] z-10 px-[5px] py-[3px] rounded-[6px] shadow-[0_4px_8px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.05)] font-medium text-white text-[11px] leading-[12px] whitespace-nowrap"
        :class="pillClass"
        :style="pillStyle"
      >
        {{ match.location }}
      </div>
    </div>

    <!-- Bracket connector -->
    <MatchBracket
      :marker-pct="pillCenterPct ?? markerPosition"
      :level="match.level"
      class="mb-[3px]"
    />

    <!-- The matched segment frames -->
    <div class="relative">
      <div
        ref="framesContainer"
        class="flex h-[50px] items-center pointer-events-none gap-x-px overflow-hidden rounded-[3px]"
      >
        <img
          v-if="!framesReady"
          :src="match.frames[0]"
          class="h-[56px] invisible absolute"
          @load="onFrameLoad"
        />

        <img
          v-for="(frameUrl, index) in distributedFrames"
          :key="index"
          :src="frameUrl"
          loading="lazy"
          decoding="async"
          class="h-[56px] shrink-0"
          :style="{ width: frameWidth + 'px' }"
        />
      </div>

      <div
        class="absolute inset-0 border-2 border-y-0 rounded-[3px] pointer-events-none"
        :class="[borderClass, fillClass]"
      ></div>

      <MatchTag
        v-if="!hasTimeline"
        :id="number"
        :level="match.level"
        class="absolute -top-2 -left-2"
      />
    </div>

    <!-- The matched segment as a playable clip -->
    <template v-if="previewUrl">
      <div class="w-full h-px my-4 bg-gray-200"></div>

      <div
        class="relative w-full overflow-hidden rounded-[4px] bg-gray-800"
        :style="{ aspectRatio: previewAspectRatio }"
      >
        <video
          ref="previewEl"
          :src="previewUrl"
          :poster="match.frames[0]"
          :controls="hasStarted"
          preload="none"
          playsinline
          class="size-full object-contain"
        ></video>

        <button
          v-if="!hasStarted"
          type="button"
          aria-label="Play matched segment"
          class="absolute inset-0 flex items-end cursor-pointer"
          @click="startPlayback"
        >
          <span
            class="m-2.5 size-9 flex items-center justify-center rounded-full border border-gray-200 bg-white"
          >
            <PlayIcon class="ml-[3px] size-4 text-gray-800" />
          </span>
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { graphql } from '@/shared/graphql/router'

export const VideoMatchFragment = graphql(`
  fragment VideoMatchFields on VideoMatch {
    id
    location
    summary
    level
    frames
    segmentUrl
    start
    end
    markers {
      hash
      color
      shine
      visible
    }
  }
`)
</script>

<script setup lang="ts">
import {
  type FragmentType,
  useFragment,
} from '@/shared/graphql/router/fragment-masking'
import MatchBracket from './MatchBracket.vue'
import { useClampedPill } from '../composables/useClampedPill'
import MatchMarkers from './MatchMarkers.vue'
import MatchTag from './matches/MatchTag.vue'
import TickerTimeline from './TickerTimeline.vue'
import { ScoreLevel, type TimelineGroup } from '@/shared/graphql/router/graphql'
import { PlayIcon, VideoIcon } from '@dacr/icons'
import {
  type ComputedRef,
  type Ref,
  computed,
  inject,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue'

const FRAME_HEIGHT = 56
const FRAME_GAP = 1
const FALLBACK_PREVIEW_ASPECT = 554 / 290

const props = defineProps<{
  match: FragmentType<typeof VideoMatchFragment>
  number: number
  side?: 'registered' | 'infringing'
  allowPlayback?: boolean
}>()

const match = useFragment(VideoMatchFragment, props.match)

const canShowPlayIcon = computed(() => props.allowPlayback !== false)

const timeline = inject<ComputedRef<TimelineGroup | undefined>>('timeline')

const timelineSide = computed(() =>
  props.side === 'registered'
    ? timeline?.value?.copyright
    : timeline?.value?.infringingFile
)

const timelineFrames = computed(() => timelineSide.value?.frames ?? [])
const duration = computed(() => timelineSide.value?.duration)
const hasTimeline = computed(() => timelineFrames.value.length > 0)

// --- Level-based styling (gray when unscored, e.g. the registered side) ---

const borderClass = computed(() => ({
  'border-x-gray-500': !match.level,
  'border-x-red-500': match.level === ScoreLevel.High,
  'border-x-yellow-500': match.level === ScoreLevel.Medium,
  'border-x-green-500': match.level === ScoreLevel.Low,
}))

const fillClass = computed(() => ({
  'bg-gray-500/10': !match.level,
  'bg-red-500/10': match.level === ScoreLevel.High,
  'bg-yellow-500/10': match.level === ScoreLevel.Medium,
  'bg-green-500/10': match.level === ScoreLevel.Low,
}))

const pillClass = computed(() => ({
  'bg-gray-500': !match.level,
  'bg-red-500': match.level === ScoreLevel.High,
  'bg-yellow-500': match.level === ScoreLevel.Medium,
  'bg-green-500': match.level === ScoreLevel.Low,
}))

// --- Frame distribution ---

const measureFrameWidth = (event: Event, widthRef: Ref<number>) => {
  const img = event.target as HTMLImageElement
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    widthRef.value = Math.round(
      (FRAME_HEIGHT / img.naturalHeight) * img.naturalWidth
    )
  }
}

const observeWidth = (
  elRef: Ref<HTMLElement | undefined>,
  widthRef: Ref<number>
) => {
  watch(elRef, (el, _, onCleanup) => {
    if (!el) {
      widthRef.value = 0
      return
    }
    const observer = new ResizeObserver(([entry]) => {
      widthRef.value = entry.contentRect.width
    })
    observer.observe(el)
    onCleanup(() => observer.disconnect())
  })
}

const distributeFrames = (
  frames: string[],
  containerWidth: number,
  frameWidth: number
) => {
  if (frames.length <= 2) return frames

  const maxFrames = Math.max(
    2,
    Math.ceil((containerWidth - frameWidth) / (frameWidth + FRAME_GAP) + 1)
  )

  if (frames.length <= maxFrames) return frames

  const result: string[] = [frames[0]]
  const innerCount = maxFrames - 2
  for (let i = 1; i <= innerCount; i++) {
    const index = Math.round((i * (frames.length - 1)) / (maxFrames - 1))
    result.push(frames[index])
  }
  result.push(frames[frames.length - 1])

  return result
}

// Matched segment frames

const framesContainer = ref<HTMLElement>()
const containerWidth = ref(0)
const frameWidth = ref(0)

const onFrameLoad = (event: Event) => measureFrameWidth(event, frameWidth)

const framesReady = computed(
  () => containerWidth.value > 0 && frameWidth.value > 0
)

const distributedFrames = computed(() =>
  framesReady.value
    ? distributeFrames(match.frames, containerWidth.value, frameWidth.value)
    : []
)

observeWidth(framesContainer, containerWidth)

// Full timeline filmstrip frames

const timelineContainer = ref<HTMLElement>()
const timelineContainerWidth = ref(0)
const timelineFrameWidth = ref(0)

const onTimelineFrameLoad = (event: Event) =>
  measureFrameWidth(event, timelineFrameWidth)

const timelineFramesReady = computed(
  () => timelineContainerWidth.value > 0 && timelineFrameWidth.value > 0
)

const distributedTimelineFrames = computed(() =>
  timelineFramesReady.value
    ? distributeFrames(
        timelineFrames.value,
        timelineContainerWidth.value,
        timelineFrameWidth.value
      )
    : []
)

observeWidth(timelineContainer, timelineContainerWidth)

// --- Match highlight position within the full timeline ---

const markerPosition = computed(() => {
  if (!duration.value) return 0
  return ((Number(match.start) + Number(match.end)) / 2 / duration.value) * 100
})

const MIN_HIGHLIGHT_WIDTH = 6

const minHighlightPct = computed(() =>
  timelineContainerWidth.value > 0
    ? (MIN_HIGHLIGHT_WIDTH / timelineContainerWidth.value) * 100
    : 0
)

const highlightWidth = computed(() => {
  if (!duration.value) return 0
  const width =
    ((Number(match.end) - Number(match.start)) / duration.value) * 100
  return Math.min(100, Math.max(width, minHighlightPct.value))
})

const highlightLeft = computed(() => {
  if (!duration.value) return 0
  const left = (Number(match.start) / duration.value) * 100
  return Math.max(0, Math.min(left, 100 - highlightWidth.value))
})

const { pillEl, pillStyle, pillCenterPct } = useClampedPill(
  timelineContainer,
  computed(() => highlightLeft.value + highlightWidth.value / 2)
)

const HIGHLIGHT_BORDER = 2

const fadeLeftWidth = computed(
  () => `calc(${highlightLeft.value}% + ${HIGHLIGHT_BORDER}px)`
)

const fadeRightWidth = computed(() => {
  const width = Math.max(0, 100 - highlightLeft.value - highlightWidth.value)
  return `calc(${width}% + ${HIGHLIGHT_BORDER}px)`
})

// --- Matched-segment clip playback ---

const previewUrl = computed(() =>
  canShowPlayIcon.value ? match.segmentUrl : null
)

// The key frames come from the same source video, so their measured aspect is
// the clip's — reserving the box before any video bytes load.
const previewAspectRatio = computed(() =>
  frameWidth.value > 0
    ? frameWidth.value / FRAME_HEIGHT
    : FALLBACK_PREVIEW_ASPECT
)

const previewEl = ref<HTMLVideoElement>()

// The poster overlay is the click target until playback starts, then it steps
// aside so the native controls own the surface.
const hasStarted = ref(false)

const startPlayback = () => {
  hasStarted.value = true
  previewEl.value?.play()
}

onBeforeUnmount(() => {
  const video = previewEl.value
  if (!video) return

  video.pause()
  video.removeAttribute('src')
  video.load()
})
</script>
